import { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import apiService from "../../../../../../service/Api";

/* =====================
   TYPES
===================== */

interface SuburbSpeedRankingItem {
  suburb: string;
  avg_speed: number;
  venue_count: number;
  min_speed: number;
  max_speed: number;
  rank: number;
}

interface SuburbSpeedRankingResponse {
  data: SuburbSpeedRankingItem[];
  total_suburbs: number;
  title: string;
}

/* =====================
   COMPONENT
===================== */

const SuburbSpeedWidget = () => {
  const [suburbs, setSuburbs] = useState<string[]>([]);
  const [speeds, setSpeeds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSuburbRanking() {
      try {
        const response = await apiService.get<SuburbSpeedRankingResponse>(
          "/api/venues/dashboard/suburb-speed-ranking/"
        );

        const items = response.data?.data ?? [];
        if (!items.length) return;

        // Sort by rank (1 = fastest)
        const sorted = [...items].sort((a, b) => a.rank - b.rank);

        setSuburbs(sorted.map((item) => item.suburb || "Unknown"));
        setSpeeds(sorted.map((item) => item.avg_speed));
      } catch (error) {
        console.error("Error fetching suburb speed ranking:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchSuburbRanking();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (!suburbs.length) return <Typography>No data available</Typography>;

  /* =====================
     DYNAMIC HEIGHT FIX
  ===================== */

  const ROW_HEIGHT = 30;
  const chartHeight = Math.max(400, suburbs.length * ROW_HEIGHT);

  return (
    <Card sx={{ height: "100%", width: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Suburb Speed Rank
        </Typography>

        <BarChart
          layout="horizontal"
          height={chartHeight}
          yAxis={[
            {
              data: suburbs,
              scaleType: "band",
              width: 160, // space for long suburb names
            },
          ]}
          series={[
            {
              data: speeds,
              label: "Upload Speed (Mbps)",
              color: "#6366F1",
            },
          ]}
          xAxis={[
            {
              label: "Upload Speed (Mbps)",
              valueFormatter: (value: number) => value.toString(),
            },
          ]}
          sx={{
            "& .MuiChartsAxis-tickLabel": {
              fontSize: 11, // subtle readability improvement
            },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default SuburbSpeedWidget;
